import DungeonInviteUI from '../ui/DungeonInviteUI.js';

/**
 * DungeonInstanceManager — Manages multiplayer dungeon instancing via
 * logical world instances on a single persistent P2P connection.
 *
 * Handles the full lifecycle:
 *   1. Invitation flow (host broadcasts invite, peers see 10s countdown)
 *   2. Instance transition (switch logical instance, no room leave/join)
 *   3. Seeded dungeon generation (all peers generate identical geometry)
 *   4. Per-level authority (oldest peer on each level runs NPC AI)
 *   5. Late joining (new peer joins mid-dungeon, receives state delta)
 *   6. Return to world (restore saved snapshot, broadcast instance change)
 */
export default class DungeonInstanceManager {
  constructor(game) {
    this.game = game;

    // Instance tracking
    this._instanceId = null;          // null = main world, "shortId" = dungeon
    this._peerInstances = new Map();  // peerId -> instanceId (null = main world)

    // Saved main world state (snapshot before entering dungeon)
    this._savedWorldState = null;
    this._savedPlayerPos = null;

    // Dungeon state
    this._dungeonConfig = null; // { theme, levelCount, seed, instanceId }
    this._isInDungeon = false;
    this._myLevel = 0; // Current level index for this peer

    // Invitation flow
    this._inviteTimeout = null;
    this._acceptedPeers = new Set();
    this._inviteUI = null;
    this._pendingInvite = null; // { config, hostPeerId }
    this._waitingForStart = false;

    // Per-peer level tracking (for per-level authority)
    // peerId -> levelIndex
    this._peerLevels = new Map();

    // Shared dungeon state (synced for late joiners)
    this._killedNPCs = new Set();
    this._openedChests = new Set();
  }

  // ────────────────────────────────────────────────────────
  //  HOST FLOW
  // ────────────────────────────────────────────────────────

  /**
   * Host initiates a multiplayer dungeon. Broadcasts invite, waits 10s, transitions.
   * @param {Object} config - { theme, levelCount }
   */
  hostDungeon(config) {
    const nm = this.game.networkManager;
    const shortId = Math.random().toString(36).slice(2, 6);
    const seed = Date.now();

    this._dungeonConfig = {
      theme: config.theme,
      levelCount: config.levelCount,
      seed,
      instanceId: shortId
    };
    this._acceptedPeers.clear();

    // Broadcast invite to all peers
    nm.broadcastAction({
      type: 'dungeon-invite',
      config: this._dungeonConfig,
      hostName: this.game.player?.characterName || 'Host',
      hostPeerId: nm.peerId
    });

    // Show countdown on host's screen too
    this._showHostCountdown();

    // After 10 seconds, transition
    this._inviteTimeout = setTimeout(() => {
      this._inviteTimeout = null;
      // Broadcast start signal so accepted peers know to transition
      nm.broadcastAction({
        type: 'dungeon-start',
        instanceId: this._dungeonConfig.instanceId
      });
      // Short delay for message propagation (same connection, fast delivery)
      setTimeout(() => this._transitionToDungeon(), 100);
    }, 10000);
  }

  /**
   * Show a countdown on the host's screen while waiting for acceptances.
   * @private
   */
  _showHostCountdown() {
    const overlay = document.createElement('div');
    overlay.id = 'dungeon-host-countdown';
    overlay.style.cssText = `
      position: fixed; top: 72px; left: 50%; transform: translateX(-50%);
      z-index: 10001; background: rgba(30,30,46,0.95); color: #cdd6f4;
      padding: 16px 28px; border-radius: 12px; font-family: sans-serif;
      box-shadow: 0 4px 20px rgba(0,0,0,0.5); text-align: center;
      border: 1px solid #45475a;
    `;

    const countdownEl = document.createElement('div');
    countdownEl.style.cssText = 'font-size: 14px; color: #a6adc8; margin-top: 8px;';
    let seconds = 10;
    countdownEl.textContent = `Starting in ${seconds}s...`;

    const peersEl = document.createElement('div');
    peersEl.id = 'dungeon-host-peers';
    peersEl.style.cssText = 'font-size: 13px; color: #89b4fa; margin-top: 6px;';
    peersEl.textContent = `${this._acceptedPeers.size} player(s) joining`;

    overlay.innerHTML = `<div style="font-size: 16px; font-weight: 600; color: #cba6f7;">Dungeon Invite Sent</div>`;
    overlay.appendChild(peersEl);
    overlay.appendChild(countdownEl);
    document.body.appendChild(overlay);

    this._hostCountdownInterval = setInterval(() => {
      seconds--;
      if (seconds <= 0) {
        clearInterval(this._hostCountdownInterval);
        this._hostCountdownInterval = null;
        overlay.remove();
        return;
      }
      countdownEl.textContent = `Starting in ${seconds}s...`;
      peersEl.textContent = `${this._acceptedPeers.size} player(s) joining`;
    }, 1000);

    this._hostCountdownOverlay = overlay;
  }

  /**
   * Called when a peer accepts the invite (received via action channel).
   * @param {string} peerId
   */
  _onPeerAccept(peerId) {
    this._acceptedPeers.add(peerId);
    // Update host countdown UI
    const peersEl = document.getElementById('dungeon-host-peers');
    if (peersEl) {
      peersEl.textContent = `${this._acceptedPeers.size} player(s) joining`;
    }
  }

  // ────────────────────────────────────────────────────────
  //  JOINER FLOW
  // ────────────────────────────────────────────────────────

  /**
   * Called when we receive a dungeon-invite from another player.
   * Shows the invite UI for 10s.
   * @param {Object} data - { config, hostName, hostPeerId }
   */
  _onDungeonInvite(data) {
    // Don't show invite if already in a dungeon
    if (this._isInDungeon) return;
    // Don't show duplicate invites
    if (this._inviteUI) return;

    this._pendingInvite = data;
    this._inviteUI = new DungeonInviteUI();
    this._inviteUI.show(
      {
        hostName: data.hostName,
        theme: data.config.theme,
        levelCount: data.config.levelCount
      },
      // onAccept
      () => {
        this._waitingForStart = true;
        this._dungeonConfig = data.config;
        // Send accept back to host
        this.game.networkManager.broadcastAction({
          type: 'dungeon-accept'
        });
      },
      // onDecline / timeout
      () => {
        this._pendingInvite = null;
        this._inviteUI = null;
        this._waitingForStart = false;
      }
    );
  }

  /**
   * Called when we receive dungeon-start from the host.
   * Only transitions if we accepted the invite.
   * @param {Object} data - { instanceId }
   */
  _onDungeonStart(data) {
    if (!this._waitingForStart) return;
    if (data.instanceId !== this._dungeonConfig?.instanceId) return;

    this._waitingForStart = false;
    if (this._inviteUI) {
      this._inviteUI.hide();
      this._inviteUI = null;
    }
    this._pendingInvite = null;

    // Transition to dungeon
    this._transitionToDungeon();
  }

  // ────────────────────────────────────────────────────────
  //  INSTANCE TRANSITION
  // ────────────────────────────────────────────────────────

  /**
   * Save state, switch to dungeon instance, generate dungeon.
   * Stays on the same P2P connection — no room leave/join.
   * @private
   */
  async _transitionToDungeon() {
    const game = this.game;

    // Clean up host countdown overlay
    if (this._hostCountdownOverlay) {
      this._hostCountdownOverlay.remove();
      this._hostCountdownOverlay = null;
    }
    if (this._hostCountdownInterval) {
      clearInterval(this._hostCountdownInterval);
      this._hostCountdownInterval = null;
    }

    // 1. Save current main world state
    this._savedWorldState = game.world.toJSON();
    this._savedPlayerPos = game.player
      ? { x: game.player.x, y: game.player.y, z: game.player.z }
      : null;

    // 2. Reset sync managers (keep alive, don't destroy/recreate)
    if (game.playerSync) game.playerSync.reset();
    if (game.npcSync) game.npcSync.reset();

    // 3. Suppress WorldSync — dungeon geometry is deterministic and never synced.
    // Save pre-dungeon world as JSON so new joiners still get main world state.
    if (game.worldSync) {
      game.worldSync._savedWorldStateJson = JSON.stringify(this._savedWorldState);
      game.worldSync._isSuppressed = true;
    }

    // 4. Clear world for dungeon
    game.world.clear();
    game.npcManager.removeAll();
    if (game.lootManager) game.lootManager.clearAll();
    game.world.forceRender();

    // 5. Set instance state
    this._myLevel = 0;
    this._peerLevels.clear();
    this._killedNPCs.clear();
    this._openedChests.clear();
    if (this._remoteKillCounts) this._remoteKillCounts.clear();
    this._isInDungeon = true;
    this._instanceId = this._dungeonConfig.instanceId;

    // 6. Broadcast instance change to all peers (instant — same connection)
    this._broadcastInstanceChange();

    // 7. Generate dungeon from shared seed
    await game.startMultiplayerDungeon(this._dungeonConfig);

    // 8. Set initial authority — peers are already known, instant!
    this._updateLevelAuthority();
  }

  /**
   * Broadcast our current instance + level to all peers.
   * Called on instance transitions and level changes.
   */
  _broadcastInstanceChange() {
    const nm = this.game.networkManager;
    if (!nm.isConnected) return;
    nm.broadcastAction({
      type: 'dungeon-instance-change',
      peerId: nm.peerId,
      inst: this._instanceId, // null for main world
      lvl: this._isInDungeon ? this._myLevel : undefined
    });
  }

  // ────────────────────────────────────────────────────────
  //  UNIFIED ACTION HANDLER
  // ────────────────────────────────────────────────────────

  /**
   * Handle all dungeon-* action channel messages.
   * Called from main.js unified action routing.
   * @param {Object} data
   * @param {string} peerId
   */
  handleAction(data, peerId) {
    switch (data.type) {
      case 'dungeon-invite':
        this._onDungeonInvite(data);
        break;
      case 'dungeon-accept':
        this._onPeerAccept(peerId);
        break;
      case 'dungeon-start':
        this._onDungeonStart(data);
        break;
      case 'dungeon-instance-change':
        // Track peer instance + level
        if (data.inst) {
          this._peerInstances.set(data.peerId, data.inst);
          this._peerLevels.set(data.peerId, data.lvl ?? 0);
        } else {
          this._peerInstances.delete(data.peerId);
          this._peerLevels.delete(data.peerId);
        }
        // Re-evaluate authority
        if (this._isInDungeon) {
          this._updateLevelAuthority();
        } else {
          this._updateMainWorldAuthority();
        }
        // Immediately update remote player visibility (hide/show sprites)
        this.game.playerSync?.reevaluateVisibility();
        break;
      case 'dungeon-level-change':
        // Compat: merge into instance-change handling
        this._peerLevels.set(data.peerId, data.levelIndex);
        this._updateLevelAuthority();
        this.game.playerSync?.reevaluateVisibility();
        break;
      case 'dungeon-chest-open':
      case 'dungeon-state-request':
      case 'dungeon-state-response':
        this._handleDungeonAction(data, peerId);
        break;
    }
  }

  /**
   * Handle dungeon-specific action channel messages (in-dungeon only).
   * @private
   */
  _handleDungeonAction(data, peerId) {
    switch (data.type) {
      case 'dungeon-chest-open': {
        // Level-prefixed key for cross-level-safe tracking
        const prefixedChestKey = `L${data.levelIndex}_${data.chestKey}`;
        this._openedChests.add(prefixedChestKey);
        // Track on generator regardless of current level (persists across transitions)
        if (this.game.dungeonGenerator) {
          this.game.dungeonGenerator._openedChests.add(prefixedChestKey);
        }
        // Apply visually if we're on that level
        if (data.levelIndex === this._myLevel) {
          // Darken the chest cuboid visually (uses raw key for DOM cuboid matching)
          this._darkenChest(data.chestKey);
          // Spawn the same loot items that the opener spawned (shared loot)
          if (data.loot && data.loot.length > 0) {
            for (const loot of data.loot) {
              this.game.lootManager?.spawnNetworkItem(loot.id, loot.itemId, loot.x, loot.y, loot.z);
            }
          }
          // Same level — apply quest progress immediately
          if (this.game.questManager) {
            this.game.questManager.onItemAcquired('dungeon-key');
          }
        } else {
          // Different level — save the objective completion so it gets restored
          // when this player later visits that level via _restoreObjectiveState()
          this._saveRemoteObjectiveCompletion(data.levelIndex, 'find_item', 'dungeon-key');
        }
        break;
      }

      case 'dungeon-state-request':
        this._handleStateRequest(peerId);
        break;

      case 'dungeon-state-response':
        this._handleStateResponse(data);
        break;
    }
  }

  // ────────────────────────────────────────────────────────
  //  PER-LEVEL AUTHORITY
  // ────────────────────────────────────────────────────────

  /**
   * Determine who is authority on the current dungeon level.
   * The oldest peer (largest Trystero ID) on each level runs NPC AI for that level.
   * Only considers peers in the same dungeon instance.
   * @private
   */
  _updateLevelAuthority() {
    const nm = this.game.networkManager;
    if (!nm.isConnected || !this.game.npcSync) return;

    // Build a set of all peers on my level in my instance
    const myPeerId = nm.peerId;
    const peersOnMyLevel = [myPeerId]; // include self

    for (const [peerId, level] of this._peerLevels) {
      if (level === this._myLevel
          && nm.peers.has(peerId)
          && this._peerInstances.get(peerId) === this._instanceId) {
        peersOnMyLevel.push(peerId);
      }
    }

    // Authority = peer with largest ID on this level
    let isAuthorityOnMyLevel = true;
    for (const peerId of peersOnMyLevel) {
      if (peerId !== myPeerId && peerId > myPeerId) {
        isAuthorityOnMyLevel = false;
        break;
      }
    }

    const wasAuthority = this.game.npcSync._isAuthority;
    this.game.npcSync.setAuthority(isAuthorityOnMyLevel);

    // Set networkControlNPCs on streamer based on authority (followers have AI disabled)
    if (this.game.dungeonGenerator?.streamer) {
      this.game.dungeonGenerator.streamer.networkControlNPCs = !isAuthorityOnMyLevel;
    }

    // If we just became authority, restore NPC behavior states
    if (!wasAuthority && isAuthorityOnMyLevel) {
      this.game.npcSync.restoreBehaviorStates();
    }
  }

  /**
   * Determine authority for main world NPC AI.
   * Only considers peers that are NOT in any dungeon instance.
   */
  _updateMainWorldAuthority() {
    const nm = this.game.networkManager;
    if (!nm.isConnected || !this.game.npcSync) return;

    // Authority = largest ID among peers NOT in any dungeon
    let isAuthority = true;
    for (const [peerId] of nm.peers) {
      if (!this._peerInstances.has(peerId) && peerId > nm.peerId) {
        isAuthority = false;
        break;
      }
    }

    const wasAuthority = this.game.npcSync._isAuthority;
    this.game.npcSync.setAuthority(isAuthority);

    // If we just became authority, restore NPC behavior states
    if (!wasAuthority && isAuthority) {
      this.game.npcSync.restoreBehaviorStates();
    }
  }

  /**
   * Check if this peer is authority on a specific level.
   * @param {number} levelIndex
   * @returns {boolean}
   */
  isAuthorityOnLevel(levelIndex) {
    const nm = this.game.networkManager;
    const myPeerId = nm.peerId;

    const peersOnLevel = [myPeerId];
    for (const [peerId, level] of this._peerLevels) {
      if (level === levelIndex
          && nm.peers.has(peerId)
          && this._peerInstances.get(peerId) === this._instanceId) {
        peersOnLevel.push(peerId);
      }
    }

    for (const peerId of peersOnLevel) {
      if (peerId !== myPeerId && peerId > myPeerId) return false;
    }
    return true;
  }

  // ────────────────────────────────────────────────────────
  //  LEVEL CHANGE
  // ────────────────────────────────────────────────────────

  /**
   * Called when the local player changes level (portal transition).
   * @param {number} newLevelIndex
   */
  onLocalLevelChange(newLevelIndex) {
    this._myLevel = newLevelIndex;

    // Broadcast instance change (includes new level)
    this._broadcastInstanceChange();

    // Re-evaluate authority for both old and new levels
    this._updateLevelAuthority();
  }

  /**
   * Get the current level of the local player.
   * @returns {number}
   */
  get currentLevel() {
    return this._myLevel;
  }

  // ────────────────────────────────────────────────────────
  //  COMBAT SYNC & LOOT
  // ────────────────────────────────────────────────────────

  /**
   * Save a remote objective completion for a level the local player isn't on.
   * Writes into dungeonGenerator._levelObjectives so _restoreObjectiveState()
   * will mark it complete when the player later visits that level.
   * @private
   */
  _saveRemoteObjectiveCompletion(levelIndex, type, itemId) {
    const gen = this.game.dungeonGenerator;
    if (!gen) return;
    let saved = gen._levelObjectives.get(levelIndex);
    if (!saved) {
      saved = [];
      gen._levelObjectives.set(levelIndex, saved);
    }
    // Find existing entry of this type (may have been saved by _saveLevelState with completed: false)
    const existing = saved.find(s => s.type === type
      && (s.itemId === itemId || (!s.itemId && !itemId)));
    if (existing) {
      // Mark existing entry as completed — _saveLevelState may have saved it as incomplete
      existing.completed = true;
    } else {
      // No existing entry — add new completed one
      saved.push({ type, completed: true, itemId: itemId || undefined });
    }
  }

  /**
   * Track a remote kill for a level the local player isn't on.
   * Increments kill count stored in _levelObjectives so kill_count and
   * kill_all objectives are restored when the player visits that level.
   * @private
   */
  _saveRemoteKill(levelIndex) {
    const gen = this.game.dungeonGenerator;
    if (!gen) return;
    // Track per-level remote kill count — used by _restoreKillState
    if (!this._remoteKillCounts) this._remoteKillCounts = new Map();
    this._remoteKillCounts.set(levelIndex, (this._remoteKillCounts.get(levelIndex) || 0) + 1);
  }

  /**
   * Darken a chest cuboid by key (remote open notification).
   * @private
   */
  _darkenChest(chestKey) {
    const gen = this.game.dungeonGenerator;
    if (!gen?.streamer) return;
    for (const [key, cuboids] of gen.streamer._chunkCuboids) {
      for (const cuboid of cuboids) {
        if (cuboid._dungeonRole === 'chest' && cuboid.getKey() === chestKey) {
          if (cuboid.element) {
            const faces = cuboid.element.querySelectorAll('.face');
            for (const face of faces) {
              face.style.filter = 'brightness(0.4)';
            }
          }
          return;
        }
      }
    }
  }

  /**
   * Broadcast chest open to all peers, including spawned loot for shared pickup.
   * @param {string} chestKey - Raw cuboid key (not level-prefixed)
   * @param {number} levelIndex
   * @param {WorldItem[]} spawnedLoot - Items spawned from the chest
   */
  broadcastChestOpen(chestKey, levelIndex, spawnedLoot = []) {
    this._openedChests.add(`L${levelIndex}_${chestKey}`);
    this.game.networkManager.broadcastAction({
      type: 'dungeon-chest-open',
      chestKey,
      levelIndex,
      loot: spawnedLoot.map(item => ({
        id: item.id, itemId: item.itemId,
        x: item.x, y: item.baseY, z: item.z
      }))
    });
  }

  // ────────────────────────────────────────────────────────
  //  LATE JOINING
  // ────────────────────────────────────────────────────────

  /**
   * Late join: enter an active dungeon instance.
   * No room transition needed — just switch instance.
   * @param {Object} config - { theme, levelCount, seed, instanceId }
   */
  async lateJoinDungeon(config) {
    this._dungeonConfig = config;
    await this._transitionToDungeon();

    // Request current dungeon state from authority
    this.game.networkManager.broadcastAction({
      type: 'dungeon-state-request'
    });
  }

  /**
   * Respond to a late joiner's state request.
   * Only the authority (on any level) should respond.
   * @private
   */
  _handleStateRequest(peerId) {
    // Only respond if we're authority on our level
    if (!this.game.npcSync?._isAuthority) return;

    const state = {
      type: 'dungeon-state-response',
      config: this._dungeonConfig,
      killedNPCs: [...this._killedNPCs],
      openedChests: [...this._openedChests],
      pickedUpItems: [...(this.game.dungeonGenerator?._pickedUpItems || [])],
      peerLevels: Object.fromEntries(this._peerLevels)
    };

    this.game.networkManager.broadcastAction(state);
  }

  /**
   * Receive state from authority after late join.
   * @private
   */
  _handleStateResponse(data) {
    // Apply killed NPCs
    if (data.killedNPCs) {
      for (const npcId of data.killedNPCs) {
        this._killedNPCs.add(npcId);
      }
    }

    // Apply opened chests
    if (data.openedChests) {
      for (const chestKey of data.openedChests) {
        this._openedChests.add(chestKey);
      }
    }

    // Apply peer levels
    if (data.peerLevels) {
      for (const [peerId, level] of Object.entries(data.peerLevels)) {
        this._peerLevels.set(peerId, level);
      }
    }

    // Apply to dungeon generator (including picked-up items for loot persistence)
    if (this.game.dungeonGenerator) {
      this.game.dungeonGenerator.applyDungeonDelta(this._killedNPCs, this._openedChests, data.pickedUpItems);
    }
  }

  // ────────────────────────────────────────────────────────
  //  RETURN TO WORLD
  // ────────────────────────────────────────────────────────

  /**
   * Leave dungeon instance and return to main world.
   * Restores saved world state. No room transition needed.
   */
  async returnToWorld() {
    const game = this.game;

    // 1. Exit dungeon mode flags
    game._dungeonMode = false;
    if (game.toolbar) game.toolbar._render();
    if (game.windowManager) game.windowManager.hide('objectives');
    this._isInDungeon = false;
    if (game.undoManager) game.undoManager.clear();
    if (game.projectileManager) game.projectileManager.clearAll();

    // 2. Restore camera/visual settings
    game.camera.minSkewAngle = undefined;
    if (game._savedSkewAngle !== null && game._savedSkewAngle !== undefined) {
      game.camera.skewAngle = game._savedSkewAngle;
    }
    if (game._savedMinZoom !== undefined) {
      game.camera.MIN_ZOOM = game._savedMinZoom;
    }
    window.sunIntensity = game._savedSunIntensity;
    if (game.cloudBackdrop) game.cloudBackdrop.setPreset('overworld');

    // 3. Hide dungeon HUD
    game.hudManager.hide();
    game.questManager.reset();

    // 4. Suppress WorldSync mutations during cleanup
    //    Dungeon cleanup calls removeCuboid for every dungeon cuboid — these must
    //    NOT be broadcast to peers (they'd destroy their own dungeon geometry).
    if (game.worldSync) {
      game.worldSync._isSuppressed = true;
    }

    // 5. Clean up dungeon geometry (safe — WorldSync is suppressed)
    game.dungeonGenerator.cleanup();
    game.npcManager.removeAll();
    game.world.clear();
    if (game.lootManager) game.lootManager.clearAll();

    // 6. Restore world from saved snapshot
    const savedWorldState = this._savedWorldState;
    const savedPlayerPos = this._savedPlayerPos;

    if (savedWorldState) {
      // Apply the snapshot inside _isApplyingRemote to suppress mutation hooks
      if (game.worldSync) game.worldSync._isApplyingRemote = true;
      try {
        game.world.fromJSON(savedWorldState, true, { batchCuboids: true });
      } finally {
        if (game.worldSync) game.worldSync._isApplyingRemote = false;
      }
    }

    // 7. Un-suppress WorldSync and mark as ready
    if (game.worldSync) {
      game.worldSync._isSuppressed = false;
      game.worldSync._savedWorldStateJson = null;
      game.worldSync._isReady = true;
    }

    // 8. Reset sync managers (keep alive)
    if (game.playerSync) game.playerSync.reset();
    if (game.npcSync) game.npcSync.reset();

    // 9. Set instance state to main world
    this._instanceId = null;
    this._broadcastInstanceChange();

    // 10. Request fresh world state from main-world peers.
    // While we were in the dungeon, WorldSync was suppressed — any edits
    // made by main-world peers were dropped. Request a full sync so we
    // pick up changes made while we were away.
    const nm = game.networkManager;
    if (nm.isConnected && nm.peerCount > 0) {
      // Allow the incoming world state to overwrite our stale snapshot
      game._hasReceivedWorldState = false;
      nm.requestWorldState();
      // Safety: if no main-world peer responds (e.g., all in dungeons),
      // re-establish ourselves after 5s so we can still serve state.
      setTimeout(() => {
        if (!game._hasReceivedWorldState) {
          game._hasReceivedWorldState = true;
        }
      }, 5000);
    }

    // 11. Clean up saved state references
    this._savedWorldState = null;
    this._savedPlayerPos = null;
    this._dungeonConfig = null;
    this._myLevel = 0;
    this._peerLevels.clear();
    this._killedNPCs.clear();
    this._openedChests.clear();
    if (this._remoteKillCounts) this._remoteKillCounts.clear();

    // 12. Restore player position
    if (savedPlayerPos && game.player) {
      game.player.x = savedPlayerPos.x;
      game.player.y = savedPlayerPos.y;
      game.player.z = savedPlayerPos.z;
      game.player.isGrounded = true;
      game.player.velocity.y = 0;
      game.orbitalCamera.setTarget(game.player.x, game.player.y, game.player.z);
      game.orbitalCamera.snapToTarget();
    }

    // 13. Re-evaluate main world authority (only among main-world peers)
    this._updateMainWorldAuthority();

    // 14. Force render
    game.world.fragmentsNeedClearing = true;
    game.world.forceRender();
    game.materialEditor?.updateCuboidList();
  }

  // ────────────────────────────────────────────────────────
  //  CLEANUP
  // ────────────────────────────────────────────────────────

  destroy() {
    if (this._inviteTimeout) {
      clearTimeout(this._inviteTimeout);
      this._inviteTimeout = null;
    }
    if (this._hostCountdownInterval) {
      clearInterval(this._hostCountdownInterval);
      this._hostCountdownInterval = null;
    }
    if (this._hostCountdownOverlay) {
      this._hostCountdownOverlay.remove();
      this._hostCountdownOverlay = null;
    }
    if (this._inviteUI) {
      this._inviteUI.hide();
      this._inviteUI = null;
    }
    this._peerLevels.clear();
    this._peerInstances.clear();
    this._killedNPCs.clear();
    this._openedChests.clear();
  }
}
